import { supabase } from '../lib/supabase';
import { generatePinImageFromJSX } from '../utils/pinGeneratorJSX';
import { uploadToStorage } from './imageService';

const PIN_FOLDER = 'pins';

/**
 * Extract keywords from URL slug for overlay text
 * Example: https://www.drecipes.com/homemade-mediterranean-stuffed-grape_245/
 * Returns: homemade mediterranean stuffed grape
 */
function extractKeywordsFromUrl(url) {
  if (!url) return '';
  
  // Remove protocol and domain
  let slug = url.replace(/^https?:\/\/[^\/]+\//, '');
  
  // Remove trailing slash and _ID suffix
  slug = slug.replace(/\/$/, '');
  slug = slug.replace(/_(\d+)$/, '');
  
  // Replace hyphens with spaces
  slug = slug.replace(/-/g, ' ');
  
  // Capitalize first letter of each word
  slug = slug.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return slug.trim();
}

/**
 * Generate Pinterest pin image from article data
 */
export async function generatePinFromArticle(
  article,
  websiteDomain = 'yoursite.com',
  templateId = 'template1',
  tags = [],
  description = '',
  boardInfo = {},
  profileId = null
) {
  console.log("🔍 SERVICE DEBUG: generatePinFromArticle called");
  console.log("🔍 SERVICE DEBUG: article:", {
    id: article?.id,
    title: article?.title,
    url: article?.url,
    featured_image_url: article?.featured_image_url ? "PRESENT" : "MISSING"
  });
  
  try {
    // Strip trailing number suffix (e.g. "Lemon Cupcakes 342" → "Lemon Cupcakes")
    const stripTrailingNumber = (t) => (t || '').replace(/[\s_\-]+\d+\s*$/, '').trim();

    // For photo: extract only the core keyword — removes SEO suffixes like ": 5 Quick Recipes"
    const extractCoreKeyword = (t) => {
      if (!t) return '';
      const core = (t.split(/[:|\u2013\u2014]/)[0] || t).trim();
      return stripTrailingNumber(core);
    };

    // Resolve the original full title (for pin metadata)
    const rawTitle = (article?.originalTitle || article?.title || '').trim();

    // Photo overlay: focus keyword core only (no title suffixes, no trailing numbers)
    const rawFocus = (article?.focus_keyword || article?.focusKeyword || article?.focus_keywords || '').trim();
    let overlayText = extractCoreKeyword(rawFocus) || extractCoreKeyword(rawTitle);

    // Fallback: extract from URL
    if (!overlayText && article?.url) {
      overlayText = extractKeywordsFromUrl(article.url);
    }

    console.log("🔍 SERVICE DEBUG: FINAL overlayText (photo):", overlayText || "(EMPTY)");

    // pinText is used for metadata (pin_title) — strip trailing number, it belongs only in the slug
    const pinText = stripTrailingNumber(rawTitle) || overlayText;
    
    // Check if we can generate a pin - need BOTH image AND text
    if (!article?.featured_image_url) {
      console.error("❌ SERVICE DEBUG: Missing featured_image_url");
      throw new Error('Article must contain featured_image_url (food image)');
    }
    
    if (!overlayText) {
      console.error("❌ SERVICE DEBUG: Missing text for pin overlay");
      console.error("🔍 SERVICE DEBUG: article.title:", article?.title || "(EMPTY)");
      console.error("🔍 SERVICE DEBUG: article.url:", article?.url || "(EMPTY)");
      throw new Error('Article must have title or URL to generate pins');
    }
    
    console.log("🔍 SERVICE DEBUG: ✅ Have both image and text - proceeding");

    // Get template URL from article data
    const templateUrl = article?.template_url || 
                       article?.template_image_url || 
                       article?.templateImageUrl || 
                       article?.templateImage ||
                       article?.template?.url ||
                       article?.template?.image_url ||
                       null;
    console.log("🔍 SERVICE DEBUG: templateUrl:", templateUrl ? "PRESENT" : "NULL");

    // Generate pin image from JSX component
    const resolvedSecondImageUrl = article?.second_image_url || article?.secondImage || article?.featured_image_url;
    console.log("🔍 SERVICE DEBUG: Calling generatePinImageFromJSX...");
    console.log("🔍 SERVICE DEBUG: article.second_image_url:", article?.second_image_url || "(missing)");
    console.log("🔍 SERVICE DEBUG: article.secondImage:", article?.secondImage || "(missing)");
    console.log("🔍 SERVICE DEBUG: resolved secondImageUrl:", resolvedSecondImageUrl || "(null — will use main image)");
    const pinImageDataUrl = await generatePinImageFromJSX({
      title: overlayText,
      imageUrl: article?.featured_image_url,
      domain: websiteDomain,
      templateId: templateId,
      templateUrl: templateUrl,
      secondImageUrl: resolvedSecondImageUrl,
      focusKeyword: overlayText,
      shopName: article?.shopName || ''
    });
    console.log("🔍 SERVICE DEBUG: generatePinImageFromJSX result:", pinImageDataUrl ? "SUCCESS" : "FAILED");
    
    if (!pinImageDataUrl) {
      console.error("❌ SERVICE DEBUG: Image generation returned empty result");
      throw new Error('Image generation returned empty result');
    }

    // Save generated image to storage
    console.log("🔍 SERVICE DEBUG: Saving to storage...");
    const storedImageUrl = await savePinImageToStorage(pinImageDataUrl, article?.id);
    console.log("🔍 SERVICE DEBUG: Stored image URL:", storedImageUrl);

    // Insert pin record into database (non-blocking — a DB error must not kill pin generation)
    console.log("🔍 SERVICE DEBUG: Inserting to library...");
    let pinRecord = null;
    try {
      pinRecord = await insertPinToLibrary({
        pinTitle: pinText,
        focusKeywords: overlayText,
        imageUrl: storedImageUrl,
        articleId: article?.id,
        templateId: templateId,
        websiteName: websiteDomain,
        profileId,
        articleUrl: article?.url || article?.articleUrl || article?.link || article?.permalink || "",
        tags,
        description: description || pinText,
        pinterestAccountId: boardInfo?.pinterestAccountId || null,
        boardId: boardInfo?.boardId || boardInfo?.id || null,
        boardName:
          boardInfo?.boardName ||
          boardInfo?.name ||
          boardInfo?.title ||
          (boardInfo?.board && boardInfo?.board.name) ||
          null
      });
    } catch (dbErr) {
      console.warn("⚠️ SERVICE DEBUG: DB insert failed (pin still available for preview):", dbErr.message);
    }

    console.log("✅ SERVICE DEBUG: Success!");
    return {
      success: true,
      imageUrl: storedImageUrl,
      pinId: pinRecord?.id,
      pinRecord
    };
  } catch (error) {
    console.error('❌ SERVICE DEBUG: Pin generation failed:', error.message);
    throw new Error(`Pin generation failed: ${error.message}`);
  }
}

/**
 * Save generated pin image to Cloudflare R2 storage
 */
async function savePinImageToStorage(imageData, articleId) {
  try {
    let imageBlob;
    if (typeof imageData === 'string' && imageData?.startsWith('data:image')) {
      // Preserve the actual MIME type from the data URL header
      const mimeMatch = imageData.match(/^data:(image\/[a-z]+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
      const base64Data = imageData?.split(',')?.[1];
      const binaryData = atob(base64Data);
      const uint8Array = new Uint8Array(binaryData?.length);
      for (let i = 0; i < binaryData?.length; i++) {
        uint8Array[i] = binaryData?.charCodeAt(i);
      }
      imageBlob = new Blob([uint8Array], { type: mimeType });
    } else if (imageData instanceof Blob) {
      imageBlob = imageData;
    } else {
      throw new Error('Invalid image data format');
    }

    // If already WebP, skip the second canvas draw — that drawImage step was the
    // primary source of softness (bilinear filtering + second lossy encode).
    const webpBlob = imageBlob.type === 'image/webp'
      ? imageBlob
      : await convertBlobToWebP(imageBlob, 0.97);

    const timestamp = Date.now();
    const filename = `pin_${articleId || 'auto'}_${timestamp}.webp`;
    const file = new File([webpBlob], filename, { type: 'image/webp' });

    // Upload to Cloudflare R2 via imageService (uses direct R2 upload)
    return await uploadToStorage(file, { folder: PIN_FOLDER });
  } catch (error) {
    console.error('Error saving pin image to storage:', error);
    throw error;
  }
}

async function convertBlobToWebP(blob, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((webpBlob) => {
        URL.revokeObjectURL(url);
        if (!webpBlob) {
          reject(new Error('Failed to convert pin image to WebP'));
          return;
        }
        resolve(webpBlob);
      }, 'image/webp', quality);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Insert pin record into pinterest_pins table
 */
export async function insertPinToLibrary(pinData) {
  console.log("🔍 LIBRARY DEBUG: insertPinToLibrary called");
  
  try {
    const { data: { user }, error: userError } = await supabase?.auth?.getUser();
    if (userError || !user) {
      console.error("❌ LIBRARY DEBUG: User not authenticated");
      throw new Error('User not authenticated');
    }

    if (!pinData?.articleUrl) {
      console.warn('❌ LIBRARY DEBUG: article_url missing');
      return null;
    }

    const tagsValue = '';
    
    const { data, error } = await supabase?.from('pinterest_pins')?.insert([
      {
        user_id: user?.id,
        profile_id: pinData?.profileId || null,
        pin_title: pinData?.pinTitle,
        focus_keywords: pinData?.focusKeywords || null,
        pin_description: pinData?.description || pinData?.pinTitle,
        article_url: pinData?.articleUrl,
        article_title: pinData?.pinTitle,
        image_url: pinData?.imageUrl,
        template_name: pinData?.templateId,
        website_name: pinData?.websiteName,
        tags: tagsValue,
        pinterest_account_id: pinData?.pinterestAccountId || null,
        board_id: pinData?.boardId || pinData?.board_id || pinData?.board?.id || null,
        board_name: pinData?.boardName || pinData?.board_name || pinData?.board?.name || null,
        status: 'ready'
      }
    ])?.select()?.single();

    if (error) {
      console.error('❌ LIBRARY DEBUG: Database insert error:', error);
      throw new Error(`Failed to insert pin record: ${error.message}`);
    }

    console.log("✅ LIBRARY DEBUG: Successfully inserted pin record:", data?.id);
    return data;
  } catch (error) {
    console.error('❌ LIBRARY DEBUG: Error inserting pin to library:', error);
    throw error;
  }
}

/**
 * Batch generate pins from multiple articles
 */
export async function batchGeneratePinsFromArticles(articles, websiteDomain = 'yoursite.com') {
  const results = [];

  for (const article of articles) {
    try {
      const result = await generatePinFromArticle(article, websiteDomain);
      results?.push({
        articleId: article?.id,
        success: true,
        ...result
      });
    } catch (error) {
      results?.push({
        articleId: article?.id,
        success: false,
        error: error?.message
      });
    }
  }

  return results;
}
